/**
 * Browser notifications with the tab closed (086): the page's half.
 *
 * Switching it on is three things in order, and each can be refused:
 *   1. the person allows notifications (the browser's own prompt, which
 *      only appears in answer to a click -- never on page load);
 *   2. the service worker is registered (public/sw.js);
 *   3. the browser subscribes with this deployment's public key, and the
 *      subscription is sent to the API.
 *
 * The state is read from the browser, not remembered: a person can revoke
 * the permission in browser settings at any time, and a switch showing "on"
 * after that would be a lie.
 */
import { api } from './api.js';

export type PushState =
  /** This browser cannot do it at all (no service workers, or no Push API). */
  | 'unsupported'
  /** The deployment has no keys, so there is nothing to switch on. */
  | 'unavailable'
  /** The person blocked notifications for this site in the browser. */
  | 'blocked'
  | 'off'
  | 'on';

export function pushSupported(): boolean {
  return typeof window !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

/** The public key, as the bytes `subscribe` wants. */
export function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function publicKey(): Promise<string | null> {
  return (await api.get<{ publicKey: string | null }>('/api/v1/push/key')).publicKey;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  return (await navigator.serviceWorker.getRegistration('/')) ?? navigator.serviceWorker.register('/sw.js', { scope: '/' });
}

export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  if (!(await publicKey())) return 'unavailable';
  if (Notification.permission === 'denied') return 'blocked';
  if (Notification.permission !== 'granted') return 'off';
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return 'off';
  // Registered in this browser -- but to whom? A shared PC signed in as
  // somebody else is "off" for this person until they switch it on.
  const r = await api.post<{ subscribed: boolean }>('/api/v1/push/subscriptions/check', { endpoint: sub.endpoint });
  return r.subscribed ? 'on' : 'off';
}

/**
 * When a notification was last handed to THIS browser's push service, or
 * null if never (or this browser is not registered to this person).
 */
export async function lastDelivered(): Promise<string | null> {
  if (!pushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return null;
  const r = await api.post<{ subscribed: boolean; lastOkAt: string | null }>('/api/v1/push/subscriptions/check', {
    endpoint: sub.endpoint,
  });
  return r.lastOkAt;
}

/** Must be called from a click: the permission prompt is refused otherwise. */
export async function enablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const key = await publicKey();
  if (!key) return 'unavailable';
  const permission = await Notification.requestPermission();
  if (permission === 'denied') return 'blocked';
  if (permission !== 'granted') return 'off';

  const reg = await registration();
  await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  // A subscription made with a different key (the deployment's keys were
  // changed) cannot be sent to. Replace it.
  const want = keyBytes(key);
  const have = sub?.options.applicationServerKey ? new Uint8Array(sub.options.applicationServerKey) : null;
  if (sub && (!have || have.length !== want.length || have.some((b, i) => b !== want[i]))) {
    await sub.unsubscribe();
    sub = null;
  }
  sub ??= await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: want });

  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  await api.post('/api/v1/push/subscriptions', {
    endpoint: json.endpoint,
    keys: { p256dh: json.keys?.p256dh, auth: json.keys?.auth },
  });
  return 'on';
}

export async function disablePush(): Promise<PushState> {
  if (!pushSupported()) return 'unsupported';
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await api.del('/api/v1/push/subscriptions', { endpoint: sub.endpoint });
    await sub.unsubscribe();
  }
  return 'off';
}
